import { askAiConsent } from './aiConsent'
import { isAiAccessError, handleAiAccessError } from './aiAccess.js'
import axios from 'axios'
import { markOnline, markOffline, beginRequest } from './netStatus.js'

// Detect native Capacitor platform via window.Capacitor bridge
// (injected by native layer before JS loads).
// Using window.Capacitor directly instead of @capacitor/core import
// to avoid potential module initialization timing issues.
const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform())
const baseURL = isNative
  ? (import.meta.env.VITE_NATIVE_API_URL || 'https://zoya.zozhnik.ru/api')
  : '/api'

// Определяем тип клиента: telegram, ios-app, android-app, pwa-ios, pwa-android
function detectClientType() {
  if (window.Telegram?.WebApp?.initData) return 'telegram'
  if (isNative) {
    const platform = window.Capacitor.getPlatform?.() || ''
    return platform === 'ios' ? 'ios-app' : 'android-app'
  }
  const ua = navigator.userAgent || ''
  if (/iPhone|iPad|iPod/.test(ua)) return 'pwa-ios'
  if (/Android/.test(ua)) return 'pwa-android'
  return 'pwa'
}

const clientType = detectClientType()

// Часовой пояс устройства: сервер запоминает его (GET /auth/me) и по нему
// считает неделю для отчета ночью с воскресенья на понедельник
function detectTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch { return '' }
}
let deviceTz = detectTz()

// Таймауты. Без них зависший запрос (iPhone потерял сеть после сна, смена
// Wi-Fi на сотовую) не падал никогда: ни шторки «нет связи», ни отказа в окне
// сохранения — только вечное «Сохраняю…» (14.10.2026, пользователь 11931).
// Распознавание и чат ждут модель, им нужен запас: nginx держит ответ
// Express 120 с (proxy_read_timeout), столько же и здесь.
const DEFAULT_TIMEOUT_MS = 20000
const UPLOAD_TIMEOUT_MS = 60000
const MODEL_TIMEOUT_MS = 120000
// Под /ai/ живут и быстрые запросы к базе (подбор продуктов, квоты, журнал):
// им хватает обычных 20 с, зависание там лучше поймать рано. Всё остальное
// под /ai/, /chat/, /photo/ ждёт модель, /admin/ — тяжёлые отчеты.
const FAST_AI_RE = /\/ai\/(lookup|direct-lookup|recipe-lookup|mapping-feedback|check-guest-quota|log-tokens|token-budget|net-report|progress)(\?|$)/
export function timeoutFor(url) {
  const u = String(url || '')
  if (/upload-photo|my-photo/.test(u)) return UPLOAD_TIMEOUT_MS
  if (FAST_AI_RE.test(u)) return DEFAULT_TIMEOUT_MS
  if (/\/(ai|chat|photo|admin)\//.test(u)) return MODEL_TIMEOUT_MS
  return DEFAULT_TIMEOUT_MS
}

const api = axios.create({
  baseURL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Type': clientType,
    'X-Ai-Access-Version': '1',
    ...(deviceTz ? { 'X-Tz': deviceTz } : {}),
  },
})

// Подставляем JWT-токен в каждый запрос; таймаут по адресу, если вызов не
// задал свой; счётчик долгих запросов для «связь медленная»
api.interceptors.request.use(async config => {
  deviceTz = detectTz()
  if (deviceTz) config.headers['X-Tz'] = deviceTz
  const token = localStorage.getItem('zoya-token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  if (config.timeout == null || config.timeout === DEFAULT_TIMEOUT_MS) config.timeout = timeoutFor(config.url)
  if (token && /^(?:\/api)?\/(ai|chat)(?:\/|$)/.test(config.url || '') && !['get','head'].includes((config.method || 'get').toLowerCase())) {
    const { data } = await api.get('/ai-consent')
    if (!data.granted && !await askAiConsent()) throw new axios.CanceledError('Обработка ИИ не разрешена')
    if (token !== localStorage.getItem('zoya-token')) throw new axios.CanceledError('Аккаунт изменился')
  }
  config.__endRequest = beginRequest()
  return config
})

// Конец запроса (любой исход) — снять его со счётчика долгих. Первым в
// цепочке ответа, чтобы сработать и для попыток, которые ретрай ниже повторит.
api.interceptors.response.use(
  response => { response.config?.__endRequest?.(); return response },
  error => { error.config?.__endRequest?.(); return Promise.reject(error) }
)

// Статус связи для шторки «нет связи»: ответа нет вовсе (обрыв, таймаут) —
// связи нет; любой ответ сервера, даже 500, — связь есть. Отменённые запросы
// не считаем. Причину определяет netStatus: интерфейс выключен — «нет
// интернета», иначе — «сервер не отвечает».
api.interceptors.response.use(
  response => { markOnline(); return response },
  error => {
    if (!axios.isCancel(error)) (error.response ? markOnline() : markOffline())
    return Promise.reject(error)
  }
)

// Retry: повторить до 2 раз при сетевых ошибках или 5xx. GET — всегда;
// запись — только с пометкой idempotent: у неё есть client_id, и сервер на
// повтор отдаст уже созданное, а не дубль (POST /diary/entries).
api.interceptors.response.use(
  response => response,
  async error => {
    const config = error.config
    if (!config || (config.method !== 'get' && !config.idempotent)) return Promise.reject(error)
    if (error.response && error.response.status < 500) return Promise.reject(error)
    config.__retryCount = (config.__retryCount || 0) + 1
    if (config.__retryCount > 2) return Promise.reject(error)
    await new Promise(r => setTimeout(r, config.__retryCount * 1000))
    return api(config)
  }
)

// Сначала открыть окно доступа, затем передать отказ вызывающему экрану.
// Каждое действие после закрытия снова открывает пейвол.
api.interceptors.response.use(
  response => response,
  async error => {
    if (isAiAccessError(error)) {
      const { useAuthStore } = await import('../store/auth.js')
      handleAiAccessError(error, useAuthStore())
    }
    return Promise.reject(error)
  }
)

// Сообщаем о 5xx. Перехватчик идёт последним, чтобы не считать попытки,
// которые ретрай выше ещё повторит, а флаг на config не даёт отчитаться по
// одному и тому же отказу трижды: повтор прогоняет всю цепочку заново, и
// финальная ошибка всплывает через каждый уровень вложенности.
//
// Сам отчет уходит через этот же api — его отказы пропускаем, иначе ошибка
// при отправке ошибки уйдёт в петлю.
api.interceptors.response.use(
  response => response,
  error => {
    const config = error.config
    if (config && !config.__failureReported && !String(config.url || '').includes('/errors/report')) {
      config.__failureReported = true
      // Lazy import: errorTracking импортирует этот файл, прямой импорт дал бы цикл
      import('../errorTracking.js')
        .then(({ reportApiFailure }) => reportApiFailure(error))
        .catch(() => {})
    }
    return Promise.reject(error)
  }
)

export default api
